import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

function resolveMaxHeight(fallbackPx) {
    if (typeof window === 'undefined') return fallbackPx;
    if (window.matchMedia('(max-width: 640px)').matches) {
        return Math.min(200, Math.round(window.innerHeight * 0.4));
    }
    return fallbackPx;
}

/**
 * Textarea that grows with content up to a capped height, then scrolls.
 * Popup/modal height follows as the field expands.
 */
const AutoGrowTextarea = React.forwardRef(function AutoGrowTextarea(
    {
        value,
        onChange,
        className,
        unstyled = true,
        minRows = 2,
        maxHeightPx = 160,
        style,
        ...props
    },
    ref,
) {
    const localRef = useRef(null);
    const [cap, setCap] = useState(maxHeightPx);

    const setRefs = (node) => {
        localRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    };

    useEffect(() => {
        const sync = () => setCap(resolveMaxHeight(maxHeightPx));
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, [maxHeightPx]);

    useLayoutEffect(() => {
        const el = localRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const next = Math.min(el.scrollHeight, cap);
        el.style.height = `${next}px`;
        el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
    }, [value, cap, minRows]);

    return (
        <Textarea
            ref={setRefs}
            unstyled={unstyled}
            rows={minRows}
            value={value}
            onChange={onChange}
            className={cn('auto-resize-textarea form-input', className)}
            style={{ ...style, maxHeight: cap }}
            {...props}
        />
    );
});

export default AutoGrowTextarea;
export { AutoGrowTextarea };
